/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { createContext, useContext, useEffect, useState } from 'react'
import Cookie from 'js-cookie'
import { useAuth0 } from '@auth0/auth0-react'
import jwtDecode from 'jwt-decode'

import { KEY_SELECTED_ROLE } from '../constants'

import {
  UserPermissions,
  isSessionExpired,
  getDefaultRoleFromToken,
  getRoles,
  getUserPermissions,
  getEvaluatorId,
  getCookieToken,
  isEMRTokenValid,
  DecodedToken,
  isProvider,
  isMedOps,
} from '../Global/Utils'
import { Roles } from '../types/enums'
import { HeaderStateProps } from '../Components/Header'
import { RedirectToAfterRoleSelection } from 'src/Global/Router'
import { FuzeHealthLogo } from 'src/Assets'
import { useApiMutation } from './Hooks/useApiMutation'
import { getAppToken as getAppTokenApi } from '../Api/common'

export interface StateProps {
  isLoggedIn: boolean
  whoAmI: null | Roles
  redirectToAfterRoleSelection: null | RedirectToAfterRoleSelection
  authorization: {
    userRoles: null | Roles[]
    userPermissions: null | UserPermissions
    evaluatorId: null | string
    networkAdmin: null | string
  }
  userInfo: {
    firstName: null | string
    lastName: null | string
    user: null | string
    id: null | string
  }
}

export interface GlobalStateI {
  setEMRAuthState: React.Dispatch<React.SetStateAction<StateProps>>
  isLoggedIn: boolean
  whoAmI: null | Roles
  redirectToAfterRoleSelection: null | RedirectToAfterRoleSelection
  authorization: {
    userRoles: null | Roles[]
    userPermissions: null | UserPermissions
    evaluatorId: null | string
    networkAdmin: null | string
  }
  userInfo: {
    firstName: null | string
    lastName: null | string
    user: null | string
    id: null | string
  }
  headerState: HeaderStateProps
  setHeaderState: React.Dispatch<React.SetStateAction<HeaderStateProps>>
  verifySession: (authToken: string) => void
  clearEMRSession: () => void
  removeSession: (redirectUrl?: string) => void
  appTokenCall: () => Promise<void>
  getWhoAmI: (authToken: string) => Roles | null
  profileModalEnabled: boolean
  setProfileModalEnabled: (enabled: boolean) => void
}

export const setSelectedRoleToLocalStore = (role: Roles) => {
  localStorage.setItem(KEY_SELECTED_ROLE, role)
}

export const getSelectedRoleFromLocalStore = (): Roles | null => {
  const selectedRole = localStorage.getItem(KEY_SELECTED_ROLE)
  return selectedRole ? (selectedRole as Roles) : null
}

const deleteKeyFromLocalStorage = (key: string) => localStorage.removeItem(key)

export const getEmptyEMRAuthState: () => StateProps = () => {
  return {
    isLoggedIn: false,
    whoAmI: null,
    redirectToAfterRoleSelection: null,
    authorization: {
      userRoles: null,
      userPermissions: null,
      evaluatorId: null,
      networkAdmin: null,
    },
    userInfo: {
      firstName: null,
      lastName: null,
      user: null,
      id: null,
    },
  }
}

export const getEMRAuthStateFromValidToken: (authToken: string) => StateProps =
  (authToken) => {
    const decodedToken: DecodedToken = jwtDecode(authToken)
    const { firstName, lastName, user, id, entityAuthorization } = decodedToken
    return {
      isLoggedIn: true,
      whoAmI: null,
      redirectToAfterRoleSelection: null,
      authorization: {
        userRoles: entityAuthorization?.roles,
        userPermissions: getUserPermissions(authToken),
        evaluatorId: isProvider(entityAuthorization?.roles)
          ? entityAuthorization?.doctor
          : null,
        networkAdmin: entityAuthorization?.roles.includes(Roles.Network)
          ? entityAuthorization?.networkAdmin
          : null,
      },
      userInfo: {
        firstName,
        lastName,
        user,
        id,
      },
    } as StateProps
  }

//@ts-ignore
export const GlobalContext = createContext<GlobalStateI>(undefined)

export const GlobalProvider = ({
  children,
}: {
  children: React.ReactChild
}) => {
  const {
    logout: auth0Logout,
    isAuthenticated,
    error: auth0Error,
    getIdTokenClaims,
  } = useAuth0()
  const selectedRole = getSelectedRoleFromLocalStore()
  const [headerState, setHeaderState] = useState<HeaderStateProps>({
    isDefault: true,
    imageLink: `/consultations`,
    imageAltText: 'Fuze Health Logo',
  })

  const [emrAuthState, setEMRAuthState] = useState<StateProps>(() => {
    const authToken = getCookieToken()
    if (!isEMRTokenValid(authToken) || !isAuthenticated) {
      return getEmptyEMRAuthState()
    }
    return {
      ...getEMRAuthStateFromValidToken(authToken),
      whoAmI: getWhoAmI(authToken),
      redirectToAfterRoleSelection: null,
    }
  })

  const [profileModalEnabled, setProfileModalEnabled] = useState(true)

  const verifySession = (authToken: string) => {
    if (isSessionExpired(authToken)) {
      removeSession(`${window.location.origin}/`)
      return
    }
    Cookie.set('token', authToken, { expires: 7 })

    const userRoles = getRoles(authToken)
    const selectedRole = getSelectedRoleFromLocalStore()

    const redirectToAfterRoleSelection =
      userRoles && userRoles?.length > 1 && !selectedRole
        ? RedirectToAfterRoleSelection.ROLES
        : null

    setEMRAuthState({
      ...getEMRAuthStateFromValidToken(authToken),
      whoAmI: getWhoAmI(authToken),
      redirectToAfterRoleSelection,
    })
  }

  const clearEMRSession = () => {
    Cookie.remove('token')
    deleteKeyFromLocalStorage(KEY_SELECTED_ROLE)
    setEMRAuthState(getEmptyEMRAuthState())
  }

  const removeSession = (redirectUrl = `${window.location.origin}/`) => {
    clearEMRSession()
    auth0Logout({ returnTo: redirectUrl })
  }

  const getLoginErrorUrl = (error: any): string => {
    const gqlError = error?.response?.data?.errors.find(
      (error: any) => error.extensions?.loginErrorType
    )
    const loginErrorType = gqlError?.extensions?.loginErrorType

    if (loginErrorType) {
      return `${window.location.origin}/login-error/?type=${loginErrorType}`
    }

    return `${window.location.origin}/login-error/`
  }

  const { mutate: getAppToken } = useApiMutation(getAppTokenApi, {
    onSuccess: async (data) => {
      const { token } = data
      await verifySession(token)
    },
    onError: (error) => {
      removeSession(getLoginErrorUrl(error))
    },
  })

  const appTokenCall = async () => {
    const tokenClaims = await getIdTokenClaims()
    if (tokenClaims?.__raw) await getAppToken(tokenClaims.__raw)
    else return removeSession(`${window.location.origin}/`)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }

  const getWhoAmI = (authToken: string) =>
    selectedRole ? (selectedRole as Roles) : getDefaultRoleFromToken(authToken)

  return (
    <GlobalContext.Provider
      value={{
        ...emrAuthState,
        setEMRAuthState,
        verifySession,
        removeSession,
        clearEMRSession,
        setHeaderState,
        appTokenCall,
        headerState,
        getWhoAmI,
        profileModalEnabled,
        setProfileModalEnabled,
      }}
    >
      {children}
    </GlobalContext.Provider>
  )
}

export function useGlobalState() {
  return useContext(GlobalContext)
}
